import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Eye, Plus, Save, Trash } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { PageLoader } from '@/components/ui/Spinner';
import { Switch } from '@/components/ui/Switch';
import { Textarea } from '@/components/ui/Textarea';
// `FormErrorAlert` nació con el login y es genérico (mensaje del backend + mensajes de Zod
// que no se pueden atribuir a un campo): las pantallas nuevas lo reutilizan.
import { FormErrorAlert } from '@/features/auth/FormErrorAlert';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import {
  EMPTY_FORM_ERRORS,
  fieldError,
  formErrorsFromApiError,
  formErrorsFromFields,
  type FormErrors,
} from '@/lib/form-errors';
import {
  LIMITS,
  validateAdminWhatsapp,
  validateAssistantName,
  validateBusinessName,
  validateCountry,
  validateCurrency,
  validateDescription,
  validateGuiaTallasFotos,
  validateLogoUrl,
  validateMaxLength,
  validateOwnPrompt,
  validatePolicies,
  validateRules,
  validateTimezone,
  validateTone,
} from './settings-validation';
import {
  GENERAL_FORM_FIELDS,
  generalSettingsFormStateFrom,
  generalSettingsPayload,
  type GeneralSettingsFormState,
} from './settings-payload';
import { useSettings, useUpdateSettings } from './use-settings';
import { SettingsLogoField } from './SettingsLogoField';
import { SettingsSizeChartField } from './SettingsSizeChartField';
import { PromptFinalDialog } from './PromptFinalDialog';
import { useTenant, useUpdateTenant } from './use-tenant';
import type { AsistentePreview } from '@/types/settings';

/**
 * Ajustes generales del negocio (pestaña "General" de `/settings`).
 *
 * Guarda en DOS endpoints con un solo botón:
 *  - `PATCH /api/tenants/:tenantId` → nombre del negocio y logo (los pinta el shell).
 *  - `PATCH /api/tenants/:tenantId/settings` → asistentes, descripción, políticas, tono,
 *    número del admin, reglas generales y —en los negocios habilitados para ello— el aviso de
 *    «foto que no está en el catálogo».
 *
 * El logo tiene además su propia subida (`POST /api/tenants/:tenantId/logo`, ver
 * `SettingsLogoField`): devuelve la URL del archivo guardado y es el `PATCH` de arriba el que la
 * persiste junto al nombre.
 *
 * El armado de los dos bodies (y el no enviar nada que no cambió) vive en
 * `settings-payload.ts`: funciones puras, verificables sin navegador.
 *
 * El backend es la autoridad: las reglas del cliente (`settings-validation.ts`) están para
 * que el error salga debajo del campo; el mensaje del servidor se pinta igual en el aviso
 * general. Sin rol ADMIN en el negocio activo el formulario queda en modo consulta, porque
 * las dos rutas exigen `requireRole('ADMIN')`.
 */

export function SettingsGeneralTab() {
  const { tenantId, canEdit } = useActiveTenantAccess();
  const tenantQuery = useTenant(tenantId);
  const settingsQuery = useSettings(tenantId);

  const updateTenant = useUpdateTenant(tenantId);
  const updateSettings = useUpdateSettings(tenantId);

  /** Lo que el usuario está editando y lo que había al cargar (base del diff del payload). */
  const [form, setForm] = useState<GeneralSettingsFormState | null>(null);
  const [loaded, setLoaded] = useState<GeneralSettingsFormState | null>(null);
  const [errors, setErrors] = useState<FormErrors>(EMPTY_FORM_ERRORS);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [rulesError, setRulesError] = useState<string | undefined>(undefined);
  /** Asistente cuyo prompt final se está viendo, o `null` si el modal está cerrado. */
  const [promptFinalDe, setPromptFinalDe] = useState<AsistentePreview | null>(null);

  /**
   * Se inicializa UNA vez por negocio. Sin esta guarda, cualquier refetch (o el propio
   * `setQueryData` que hace la mutación al guardar) pisaría lo que el usuario está
   * escribiendo.
   */
  const initializedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!tenantId || !tenantQuery.data || !settingsQuery.data) return;
    if (initializedFor.current === tenantId) return;

    initializedFor.current = tenantId;
    const initial = generalSettingsFormStateFrom(tenantQuery.data, settingsQuery.data);
    setForm(initial);
    setLoaded(initial);
  }, [tenantId, tenantQuery.data, settingsQuery.data]);

  if (tenantQuery.isPending || settingsQuery.isPending) {
    return <PageLoader label="Cargando los ajustes del negocio…" />;
  }

  if (tenantQuery.isError || settingsQuery.isError) {
    return <Alert>{getErrorMessage(tenantQuery.error ?? settingsQuery.error)}</Alert>;
  }

  // La primera vuelta tras llegar los datos todavía no ejecutó el efecto que arma el form.
  if (!form || !loaded) return <PageLoader label="Cargando los ajustes del negocio…" />;

  const payload = generalSettingsPayload(form, loaded);
  const dirty = payload.tenant !== null || payload.settings !== null;
  const saving = updateTenant.isPending || updateSettings.isPending;

  /** Cambia un campo y limpia el aviso de "guardado" y el error de ese campo. */
  function updateField(field: keyof GeneralSettingsFormState, value: string) {
    setSaved(false);
    setSubmitError(null);
    setErrors((current) => {
      if (!current.fieldErrors[field] && current.formMessages.length === 0) return current;

      const fieldErrors = { ...current.fieldErrors };
      delete fieldErrors[field];
      return { formMessages: [], fieldErrors };
    });
    setForm((current) => (current ? { ...current, [field]: value } : current));
  }

  function updateRules(rules: string[]) {
    setSaved(false);
    setSubmitError(null);
    setRulesError(validateRules(rules));
    setForm((current) => (current ? { ...current, reglasGenerales: rules } : current));
  }

  /**
   * Interruptor de «foto que no está en el catálogo»: `updateField` es para texto, así que el boolean
   * tiene su propio camino (mismo criterio: limpia el aviso de guardado y el error de su campo).
   */
  function updateAvisoFoto(avisar: boolean) {
    setSaved(false);
    setSubmitError(null);
    setErrors((current) => {
      if (!current.fieldErrors.avisarFotoNoCatalogo && current.formMessages.length === 0) {
        return current;
      }

      const fieldErrors = { ...current.fieldErrors };
      delete fieldErrors.avisarFotoNoCatalogo;
      return { formMessages: [], fieldErrors };
    });
    setForm((current) => (current ? { ...current, avisarFotoNoCatalogo: avisar } : current));
  }

  /**
   * Guía de tallas (24-sep-2026): son una LISTA de URLs (hasta 2), así que no vale `updateField`, que es
   * para texto. Mismo trato del error: al tocar el campo se borra el mensaje de ESE campo.
   */
  function updateGuiaTallasFotos(fotos: string[]) {
    setSaved(false);
    setSubmitError(null);
    setErrors((current) => {
      if (!current.fieldErrors.guiaTallasFotos && current.formMessages.length === 0) return current;

      const fieldErrors = { ...current.fieldErrors };
      delete fieldErrors.guiaTallasFotos;
      return { formMessages: [], fieldErrors };
    });
    setForm((current) => (current ? { ...current, guiaTallasFotos: fotos } : current));
  }

  function handleReset() {
    setForm(loaded);
    setErrors(EMPTY_FORM_ERRORS);
    setRulesError(undefined);
    setSubmitError(null);
    setSaved(false);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form || !loaded) return;

    const rules = validateRules(form.reglasGenerales);
    const nextErrors = formErrorsFromFields({
      nombreEmpresa: validateBusinessName(form.nombreEmpresa),
      logoUrl: validateLogoUrl(form.logoUrl),
      nombreAsistenteVentas: validateAssistantName(
        form.nombreAsistenteVentas,
        'El nombre del asistente de ventas',
      ),
      nombreAsistentePostventa: validateAssistantName(
        form.nombreAsistentePostventa,
        'El nombre del asistente de postventa',
      ),
      descripcionEmpresa: validateDescription(form.descripcionEmpresa),
      politicas: validatePolicies(form.politicas),
      personalidadTono: validateTone(form.personalidadTono),
      adminWhatsappNumber: validateAdminWhatsapp(form.adminWhatsappNumber),
      pais: validateCountry(form.pais),
      moneda: validateCurrency(form.moneda),
      zonaHoraria: validateTimezone(form.zonaHoraria),
      promptVentas: validateOwnPrompt(form.promptVentas, 'El prompt de ventas'),
      promptPostventa: validateOwnPrompt(form.promptPostventa, 'El prompt de postventa'),
      mensajeFotoNoCatalogo: validateMaxLength(
        form.mensajeFotoNoCatalogo,
        LIMITS.mensajeFotoNoCatalogo,
        'El mensaje para el cliente',
      ),
      guiaTallasFotos: validateGuiaTallasFotos(form.guiaTallasFotos),
      reglasGenerales: rules,
    });

    setRulesError(rules);
    setSaved(false);

    if (Object.keys(nextErrors.fieldErrors).length > 0) {
      setErrors(nextErrors);
      setSubmitError(null);
      return;
    }

    const body = generalSettingsPayload(form, loaded);
    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);

    try {
      // Secuencial a propósito: si el backend rechaza el segundo, el usuario ve el error del
      // que falló en vez de dos peticiones cruzadas.
      if (body.tenant) await updateTenant.mutateAsync(body.tenant);
      if (body.settings) await updateSettings.mutateAsync(body.settings);

      // Lo guardado pasa a ser la nueva referencia del diff (el botón se desactiva solo).
      setLoaded({ ...form });
      setSaved(true);
    } catch (error) {
      setErrors(formErrorsFromApiError(error, GENERAL_FORM_FIELDS));
      setSubmitError(getErrorMessage(error));
    }
  }


  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-6">
      <FormErrorAlert message={submitError} errors={errors} />

      {saved ? (
        <Alert tone="success">
          Cambios guardados. El bot ya usa estos ajustes en el próximo mensaje.
        </Alert>
      ) : null}

      {!canEdit ? (
        <Alert tone="info">
          Tu rol en este negocio es <strong className="font-semibold">Agente humano</strong>: puedes
          ver los ajustes, pero solo un administrador puede cambiarlos.
        </Alert>
      ) : null}

      {/* ------------------------------ Negocio ------------------------------ */}
      <Card>
        <CardHeader>
          <CardTitle>Datos del negocio</CardTitle>
          <CardDescription>
            El nombre y el logo que ves en el propio panel. El logo se puede subir desde tu
            computador o pegar como URL de una imagen ya publicada.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-5">
          <div className="flex items-center gap-4">
            <Avatar name={form.nombreEmpresa || 'Negocio'} src={form.logoUrl || null} size="lg" />
            <p className="text-caption text-ink-muted">
              El logo aparece en el selector de negocio. Si la URL no carga, se ven las iniciales
              del nombre.
            </p>
          </div>

          <Field
            label="Nombre del negocio"
            htmlFor="nombreEmpresa"
            required
            error={fieldError(errors, 'nombreEmpresa')}
          >
            <Input
              id="nombreEmpresa"
              name="nombreEmpresa"
              value={form.nombreEmpresa}
              disabled={!canEdit}
              invalid={Boolean(fieldError(errors, 'nombreEmpresa'))}
              aria-describedby={
                fieldError(errors, 'nombreEmpresa') ? 'nombreEmpresa-error' : undefined
              }
              onChange={(event) => updateField('nombreEmpresa', event.target.value)}
            />
          </Field>

          <SettingsLogoField
            value={form.logoUrl}
            onChange={(logoUrl) => updateField('logoUrl', logoUrl)}
            disabled={!canEdit}
            error={fieldError(errors, 'logoUrl')}
          />
        </CardContent>
      </Card>

      {/* ----------------------------- Asistentes ---------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Asistentes y tono</CardTitle>
          <CardDescription>
            Cómo se presenta el bot y qué sabe de tu negocio: se inyecta en el prompt de cada
            asistente (el de ventas y el de postventa son prompts distintos).
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Asistente de ventas"
              htmlFor="nombreAsistenteVentas"
              hint="Con este nombre se presenta al atender a un cliente nuevo."
              error={fieldError(errors, 'nombreAsistenteVentas')}
            >
              <Input
                id="nombreAsistenteVentas"
                name="nombreAsistenteVentas"
                placeholder="Ej. Sara"
                value={form.nombreAsistenteVentas}
                disabled={!canEdit}
                invalid={Boolean(fieldError(errors, 'nombreAsistenteVentas'))}
                onChange={(event) => updateField('nombreAsistenteVentas', event.target.value)}
              />
            </Field>

            <Field
              label="Asistente de postventa"
              htmlFor="nombreAsistentePostventa"
              hint="Atiende a clientes que ya tienen una orden (estado, guía, dudas de uso)."
              error={fieldError(errors, 'nombreAsistentePostventa')}
            >
              <Input
                id="nombreAsistentePostventa"
                name="nombreAsistentePostventa"
                placeholder="Ej. Julián"
                value={form.nombreAsistentePostventa}
                disabled={!canEdit}
                invalid={Boolean(fieldError(errors, 'nombreAsistentePostventa'))}
                onChange={(event) => updateField('nombreAsistentePostventa', event.target.value)}
              />
            </Field>
          </div>

          <Field
            label="Personalidad y tono"
            htmlFor="personalidadTono"
            hint={`Formal, cercano, con o sin emojis… (máx. ${LIMITS.personalidadTono} caracteres)`}
            error={fieldError(errors, 'personalidadTono')}
          >
            <Textarea
              id="personalidadTono"
              name="personalidadTono"
              rows={3}
              placeholder="Ej. Cercano y directo, tutea, usa emojis con moderación, nunca presiona para cerrar la venta."
              value={form.personalidadTono}
              disabled={!canEdit}
              invalid={Boolean(fieldError(errors, 'personalidadTono'))}
              onChange={(event) => updateField('personalidadTono', event.target.value)}
            />
          </Field>

          <Field
            label="Descripción del negocio"
            htmlFor="descripcionEmpresa"
            hint={`Qué vendes y a quién (máx. ${LIMITS.descripcionEmpresa} caracteres)`}
            error={fieldError(errors, 'descripcionEmpresa')}
          >
            <Textarea
              id="descripcionEmpresa"
              name="descripcionEmpresa"
              rows={4}
              value={form.descripcionEmpresa}
              disabled={!canEdit}
              invalid={Boolean(fieldError(errors, 'descripcionEmpresa'))}
              onChange={(event) => updateField('descripcionEmpresa', event.target.value)}
            />
          </Field>

          <Field
            label="Políticas (envíos, devoluciones, garantía)"
            htmlFor="politicas"
            hint={`El bot responde con esto cuando le preguntan (máx. ${LIMITS.politicas} caracteres)`}
            error={fieldError(errors, 'politicas')}
          >
            <Textarea
              id="politicas"
              name="politicas"
              rows={4}
              value={form.politicas}
              disabled={!canEdit}
              invalid={Boolean(fieldError(errors, 'politicas'))}
              onChange={(event) => updateField('politicas', event.target.value)}
            />
          </Field>

          <Field
            label="Número de WhatsApp del administrador"
            htmlFor="adminWhatsappNumber"
            hint="Solo para las notificaciones internas (nueva venta, cancelación, atención humana). Escríbelo con el indicativo del país: puede ir con «+» y espacios (el backend lo normaliza antes de enviarlo)."
            error={fieldError(errors, 'adminWhatsappNumber')}
          >
            <Input
              id="adminWhatsappNumber"
              name="adminWhatsappNumber"
              placeholder="+57 300 1234567"
              value={form.adminWhatsappNumber}
              disabled={!canEdit}
              invalid={Boolean(fieldError(errors, 'adminWhatsappNumber'))}
              onChange={(event) => updateField('adminWhatsappNumber', event.target.value)}
            />
          </Field>
        </CardContent>
      </Card>

      {/* ---------------- País, moneda y zona horaria (contexto) -------------- */}
      <Card>
        <CardHeader>
          <CardTitle>País, moneda y zona horaria</CardTitle>
          <CardDescription>
            Dónde y con qué atiende el negocio: el bot usa el país para el indicativo del teléfono y
            para validar la ciudad con el departamento, la moneda para hablar de precios en el
            resumen y la zona horaria para saber qué hora es donde atiendes. Si los dejas vacíos el
            bot trabaja como hasta ahora (Colombia, COP y hora de Bogotá).
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="País"
              htmlFor="pais"
              hint="Ej. Colombia, México, España. También puede ir el código ISO (CO, MX, ES)."
              error={fieldError(errors, 'pais')}
            >
              <Input
                id="pais"
                name="pais"
                placeholder="Colombia"
                value={form.pais}
                disabled={!canEdit}
                invalid={Boolean(fieldError(errors, 'pais'))}
                onChange={(event) => updateField('pais', event.target.value)}
              />
            </Field>

            <Field
              label="Moneda"
              htmlFor="moneda"
              hint="Ej. COP, MXN, EUR. El bot la usa al mostrar totales en el resumen."
              error={fieldError(errors, 'moneda')}
            >
              <Input
                id="moneda"
                name="moneda"
                placeholder="COP"
                value={form.moneda}
                disabled={!canEdit}
                invalid={Boolean(fieldError(errors, 'moneda'))}
                onChange={(event) => updateField('moneda', event.target.value)}
              />
            </Field>
          </div>

          <Field
            label="Zona horaria"
            htmlFor="zonaHoraria"
            hint="Formato IANA: America/Bogota, America/Mexico_City, Europe/Madrid, America/Argentina/Buenos_Aires."
            error={fieldError(errors, 'zonaHoraria')}
          >
            <Input
              id="zonaHoraria"
              name="zonaHoraria"
              placeholder="America/Bogota"
              value={form.zonaHoraria}
              disabled={!canEdit}
              invalid={Boolean(fieldError(errors, 'zonaHoraria'))}
              onChange={(event) => updateField('zonaHoraria', event.target.value)}
            />
          </Field>
        </CardContent>
      </Card>

      {/* ---------------------- Guía de tallas (negocio) --------------------- */}
      {/* Una sola imagen por NEGOCIO (24-sep-2026): el bot la manda cuando el cliente pregunta por
          tallas. Es lo que pidió el negocio de zapatillas; los demás no ven nada nuevo salvo la
          tarjeta, que está vacía por defecto y no cambia ninguna respuesta. */}
      <Card>
        <CardHeader>
          <CardTitle>Guía de tallas</CardTitle>
          <CardDescription>
            Hasta dos imágenes por negocio para los comercios que manejan tabla de tallas (por ejemplo la
            del calzado y la de la ropa). El bot las manda juntas al cliente cuando pregunta por tallas
            («¿qué talla me queda?», «mándame la tabla de tallas»), sin pasar por la IA y al instante. Sin
            guía cargada, esas preguntas las contesta con el catálogo como cualquier otra.
          </CardDescription>
        </CardHeader>

        <CardContent>
          <SettingsSizeChartField
            value={form.guiaTallasFotos}
            onChange={updateGuiaTallasFotos}
            disabled={!canEdit}
            error={fieldError(errors, 'guiaTallasFotos')}
          />
        </CardContent>
      </Card>

      {/* ------------- Fotos que no están en el catálogo (UN negocio) --------- */}
      {/* La tarjeta solo existe para los negocios que el backend tiene en `VISION_AVISO_ADMIN_TENANTS`:
          es una solicitud especial de un negocio, así que los demás no ven nada (y el 403 del backend
          impide activarla a mano por API). */}
      {settingsQuery.data?.avisoFotoAdminDisponible ? (
        <Card>
          <CardHeader>
            <CardTitle>Pedidos que no están en el catálogo</CardTitle>
            <CardDescription>
              Cuando el cliente manda una foto que el sistema no reconoce en el catálogo —o pide por
              texto un modelo que no está—, en vez de responderle que no lo tenemos el bot le dice que
              vamos a validar si el modelo está disponible, y a ti te llega un aviso por WhatsApp (con
              la foto del cliente, cuando la mandó) y el enlace directo a la conversación para que la
              revises y le contestes a mano.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-5">
            <div className="flex items-start gap-3">
              <Switch
                id="avisarFotoNoCatalogo"
                checked={form.avisarFotoNoCatalogo}
                disabled={!canEdit}
                label="Avisarme por WhatsApp cuando pidan algo que no esté en el catálogo"
                onChange={updateAvisoFoto}
              />
              <div>
                <p className="text-body font-medium text-ink">
                  Avisarme por WhatsApp cuando pidan algo que no esté en el catálogo
                </p>
                <p className="text-caption text-ink-muted">
                  El aviso va al número del administrador configurado arriba. Con el interruptor
                  apagado el bot vuelve al comportamiento de siempre: si era una foto, «no logré
                  identificar el producto, ¿me escribes el nombre o la referencia?»; si era texto,
                  ofrece alternativas del catálogo.
                </p>
              </div>
            </div>

            <Field
              label="Lo que le dice el bot al cliente"
              htmlFor="mensajeFotoNoCatalogo"
              hint={`Vacío = el texto de fábrica («vamos a validar si tenemos ese modelo disponible»). Sirve para los dos casos: la foto que no se reconoce y el modelo que piden por texto. Máx. ${LIMITS.mensajeFotoNoCatalogo} caracteres`}
              error={fieldError(errors, 'mensajeFotoNoCatalogo')}
            >
              <Textarea
                id="mensajeFotoNoCatalogo"
                name="mensajeFotoNoCatalogo"
                rows={3}
                placeholder="Ej. ¡Gracias por la foto! 📷 Dame un momentico: voy a validar si tenemos ese modelo disponible y te confirmo 🙌"
                value={form.mensajeFotoNoCatalogo}
                disabled={!canEdit}
                invalid={Boolean(fieldError(errors, 'mensajeFotoNoCatalogo'))}
                onChange={(event) => updateField('mensajeFotoNoCatalogo', event.target.value)}
              />
            </Field>
          </CardContent>
        </Card>
      ) : null}

      {/* ------------------ Prompt propio del negocio (avanzado) -------------- */}
      <Card>
        <CardHeader
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Eye size={14} />}
                onClick={() => setPromptFinalDe('VENTAS')}
              >
                Ver el de ventas
              </Button>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Eye size={14} />}
                onClick={() => setPromptFinalDe('POSTVENTA')}
              >
                Ver el de postventa
              </Button>
            </div>
          }
        >
          <CardTitle>Prompt propio del negocio (avanzado)</CardTitle>
          <CardDescription>
            Escribe con tus palabras cómo debe atender el bot (qué ofrecer, qué no decir, cómo cerrar…).
            El sistema pega tu texto en una sección con prioridad alta y arma el resto por su cuenta:
            catálogo, medios de pago, pedidos, fecha y hora, y el formato del pedido. Por eso no escribas
            datos que ya están en el panel ni variables: no hacen falta y no se pueden romper. Deja el
            campo vacío y el bot usa el prompt de fábrica.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-5">
          <Field
            label="Instrucciones propias para ventas"
            htmlFor="promptVentas"
            hint={`Lo que quieras que el bot haga distinto a lo de fábrica (máx. ${LIMITS.promptPropio} caracteres)`}
            error={fieldError(errors, 'promptVentas')}
          >
            <Textarea
              id="promptVentas"
              name="promptVentas"
              rows={8}
              placeholder={
                'Ej. Antes de dar precios, pregunta siempre para qué ocasión es.\n' +
                'Ej. Nunca prometas entrega el mismo día.\n' +
                'Ej. Si el cliente duda del color, ofrécele mandarle fotos.'
              }
              value={form.promptVentas}
              disabled={!canEdit}
              invalid={Boolean(fieldError(errors, 'promptVentas'))}
              onChange={(event) => updateField('promptVentas', event.target.value)}
            />
          </Field>

          <Field
            label="Instrucciones propias para postventa"
            htmlFor="promptPostventa"
            hint="Aplica al asistente de pedidos en curso (estado, guía, garantías). Opcional."
            error={fieldError(errors, 'promptPostventa')}
          >
            <Textarea
              id="promptPostventa"
              name="promptPostventa"
              rows={6}
              placeholder={
                'Ej. Si preguntan por una devolución, pide siempre el número de pedido y el motivo.\n' +
                'Ej. No des fechas exactas de entrega: solo el rango de 1 a 5 días.'
              }
              value={form.promptPostventa}
              disabled={!canEdit}
              invalid={Boolean(fieldError(errors, 'promptPostventa'))}
              onChange={(event) => updateField('promptPostventa', event.target.value)}
            />
          </Field>

          <p className="text-caption text-ink-muted">
            ¿Dudas de qué está recibiendo el bot? Abre «Ver el de ventas»: ahí está el prompt final,
            armado por el sistema, tal cual se le manda al modelo.
          </p>
        </CardContent>
      </Card>

      {/* --------------------------- Reglas generales ------------------------- */}
      <Card>
        <CardHeader
          actions={
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Plus size={14} />}
              disabled={!canEdit || form.reglasGenerales.length >= LIMITS.reglas}
              onClick={() => updateRules([...form.reglasGenerales, ''])}
            >
              Agregar regla
            </Button>
          }
        >
          <CardTitle>Reglas generales</CardTitle>
          <CardDescription>
            Obligatorias para los dos asistentes y con prioridad sobre el tono y el catálogo: se
            inyectan al inicio del prompt (hasta {LIMITS.reglas} reglas de {LIMITS.regla}{' '}
            caracteres).
          </CardDescription>
        </CardHeader>

        <CardContent>
          {rulesError ? (
            <Alert tone="warning" className="mb-4">
              {rulesError}
            </Alert>
          ) : null}

          {form.reglasGenerales.length === 0 ? (
            <EmptyState
              title="Todavía no hay reglas"
              description="Ejemplos: «no confirmar disponibilidad sin verificar el inventario», «no ofrecer descuentos no autorizados», «no mencionar la dirección física del negocio»."
              action={
                canEdit ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={<Plus size={14} />}
                    onClick={() => updateRules([''])}
                  >
                    Agregar la primera regla
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <ul className="space-y-2">
              {/* El índice como key es correcto aquí: las filas son texto plano y se editan en
                  su sitio (no hay estado interno por fila que se pueda mezclar al borrar). */}
              {form.reglasGenerales.map((rule, index) => (
                <li key={index} className="flex items-center gap-3">
                  <span className="tabular w-5 shrink-0 text-caption text-ink-faint">
                    {index + 1}.
                  </span>

                  <Input
                    value={rule}
                    placeholder="ej. No confirmar disponibilidad sin verificar el inventario"
                    disabled={!canEdit}
                    aria-label={`Regla ${index + 1}`}
                    onChange={(event) => {
                      const next = [...form.reglasGenerales];
                      next[index] = event.target.value;
                      updateRules(next);
                    }}
                  />

                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={!canEdit}
                    aria-label={`Eliminar la regla ${index + 1}`}
                    onClick={() =>
                      updateRules(form.reglasGenerales.filter((_, position) => position !== index))
                    }
                  >
                    <Trash size={16} />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <p className="mt-3 text-micro text-ink-faint">Las filas vacías se descartan al guardar.</p>
        </CardContent>
      </Card>

      {/* ------------------------------- Acciones ----------------------------- */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="secondary" onClick={handleReset} disabled={!canEdit || !dirty || saving}>
          Descartar cambios
        </Button>

        <Button
          type="submit"
          leftIcon={<Save size={16} />}
          loading={saving}
          disabled={!canEdit || !dirty}
        >
          Guardar cambios
        </Button>
      </div>

      {promptFinalDe ? (
        <PromptFinalDialog
          open
          onClose={() => setPromptFinalDe(null)}
          tenantId={tenantId}
          asistente={promptFinalDe}
          hayCambiosSinGuardar={dirty}
        />
      ) : null}
    </form>
  );
}



